import { useContext, useEffect, useRef, isValidElement, useMemo, useState, type JSX } from "react";
import { Badge, Box, Card, IconButton, Drawer, Tooltip, useMediaQuery } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";
import styles from "./FiltersBtn.module.scss";
import { useAtomValue, useAtom } from "jotai";
import { textColorState, themeState, filtersDrawerOpenState } from "@/lib/store";
import { ContentLayoutContext, ModuleContext } from "@/lib/Context";
import ObfuscatedFiltersDialog from "../data-grid/ObfuscatedFiltersDialog";

interface Props {
    filters: JSX.Element;
}

const FiltersBtn = ({ filters }: Props) => {
    const textColor = useAtomValue(textColorState);
    const { filteredAmount } = useContext(ModuleContext);
    const { disableFilters } = useContext(ContentLayoutContext);
    const theme = useAtomValue(themeState);
    const [localDrawerOpen, setLocalDrawerOpen] = useState(false);
    const [globalDrawerOpen, setGlobalDrawerOpen] = useAtom(filtersDrawerOpenState);
    const [visitorDialogOpen, setVisitorDialogOpen] = useState(false);

    const isMobile = useMediaQuery("(max-width: 600px)");

    const filtersContainerRef = useRef<HTMLDivElement>(null);

    const uniqueId = useMemo(() => {
        return `filters-drawer-${Math.random().toString(36).substring(2, 11)}`;
    }, []);

    useEffect(() => {
        if (!globalDrawerOpen) {
            setLocalDrawerOpen(false);
        }
    }, [globalDrawerOpen]);

    const handleClick = () => {
        setLocalDrawerOpen(true);
        // Reflect open state in the shared atom so callers can close the drawer by setting it false (used by Apply buttons)
        setGlobalDrawerOpen(true);
    };

    const handleClose = () => {
        setLocalDrawerOpen(false);
        setGlobalDrawerOpen(false);
    };

    const open = localDrawerOpen;
    const id = open ? uniqueId : undefined;
    const anchorPosition = isMobile ? "bottom" : "right";

    if (!filters || !isValidElement(filters)) return null;

    return (
        <Box sx={{ display: "flex", alignItems: "center" }}>
            <Tooltip title="Filters" arrow>
                {/* The accessible name starts with the visible badge count so it matches the visible label (WCAG 2.5.3) */}
                <IconButton aria-label={`${filteredAmount || 0} filters applied`} className={filteredAmount ? styles.filtered : styles.button} sx={{ padding: 0 }} onClick={handleClick}>
                    <Badge badgeContent={filteredAmount} color="primary">
                        <FilterListIcon style={{ color: textColor }} />
                    </Badge>
                </IconButton>
            </Tooltip>

            <Drawer
                anchor={anchorPosition}
                id={id}
                open={open}
                onClose={handleClose}
                slotProps={{
                    paper: {
                        sx: {
                            paddingTop: "env(safe-area-inset-top)",
                            backgroundColor: "transparent",
                            display: "flex",
                            alignItems: "normal",
                            justifyContent: "flex-start",

                            "& .MuiSelect-icon": {
                                color: theme === "dark" && textColor,
                            },
                        },
                    },
                }}
            >
                <Card sx={{ p: 2, maxWidth: "100%", height: "100%" }} ref={filtersContainerRef}>
                    {isMobile && (
                        <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-end" }}>
                            <IconButton aria-label="Close filters" sx={{ padding: 0 }} onClick={handleClose}>
                                <CloseIcon style={{ color: "grey" }} />
                            </IconButton>
                        </Box>
                    )}
                    <Box sx={{ position: "relative" }}>
                        {filters}
                        {disableFilters && <Box sx={{ position: "absolute", inset: 0, cursor: "pointer", zIndex: 10 }} onClick={() => setVisitorDialogOpen(true)} />}
                    </Box>
                </Card>
            </Drawer>
            <ObfuscatedFiltersDialog open={visitorDialogOpen} onClose={() => setVisitorDialogOpen(false)} />
        </Box>
    );
};

export default FiltersBtn;
